import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  FormCheck,
} from "react-bootstrap";
import { fromZonedTime } from "date-fns-tz";
import { parse } from "date-fns";
import axios from "axios";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

export const showToast = (title, icon = "success") => {
  Swal.fire({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    icon,
    title,
  });
};

export default function TimeSlotModal({
  show,
  onHide,
  slot,
  slug,
  bookingId,
  eventSettings = {},
  eventLimits = {},
  eventId,
  use24Hour,
}) {
  /* ---------- Form state ---------- */
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [guests, setGuests] = useState("");
  const [customFields, setCustomFields] = useState([]);
  const [customAnswers, setCustomAnswers] = useState({});
  const [totalBookings, setTotalBookings] = useState(0);
  const [bookingHistory, setBookingHistory] = useState([]);

  /* ---------- Payment state ---------- */
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showGatewayModal, setShowGatewayModal] = useState(false); // <-- both gateways
  const [showRazorpayModal, setShowRazorpayModal] = useState(false); // <-- **NEW**
  const [selectedGateway, setSelectedGateway] = useState("");
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [stripeRedirecting, setStripeRedirecting] = useState(false);

  const formRef = useRef();
  /* ---------- CHECK URL FOR STRIPE RESULT ---------- */
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const paymentStatus = query.get("payment");
    const bookingIdParam = query.get("bookingId"); // For success

    if (paymentStatus === "success") {
      showToast("Payment successful! Booking confirmed.", "success");
      // Optional: Clean URL after success
      if (bookingIdParam) {
        window.history.replaceState(
          {},
          "",
          window.location.pathname + `?bookingId=${bookingIdParam}`
        );
      } else {
        window.history.replaceState({}, "", window.location.pathname);
      }
      // Optional: Auto-close modal or redirect after 3s
      setTimeout(() => {
        if (onHide) onHide(); // Close modal
      }, 3000);
    } else if (paymentStatus === "cancel") {
      showToast("Payment cancelled. You can try again.", "warning");
      // Clean URL to remove ?payment=cancel
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // Runs once on component mount

  /* ---------- Load Razorpay once ---------- */
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => setRazorpayLoaded(true);
    script.onerror = () => showToast("Razorpay failed to load", "error");
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  /* ---------- Load totals / history / questions ---------- */
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/event/${eventId}/total_bookings`)
      .then((r) => setTotalBookings(r.data.count))
      .catch(() => {});
  }, [eventId]);

  useEffect(() => {
    if (email && eventId) {
      axios
        .post(`${API_BASE_URL}/event/check-frequency`, {
          email,
          event_id: eventId,
        })
        .then((r) => setBookingHistory(r.data.history))
        .catch(() => {});
    }
  }, [email, eventId]);

  useEffect(() => {
    if (show && slug) {
      axios
        .get(`${API_BASE_URL}/event/${slug}/questions`)
        .then((r) => setCustomFields(r.data))
        .catch(() => {});
    }
  }, [show, slug]);

  const handleCustomFieldChange = (id, value) => {
    setCustomAnswers((prev) => ({ ...prev, [id]: value }));
  };

  /* ---------- BOOKING + PAYMENT TRIGGER ---------- */
  const handleConfirm = async (e) => {
    e.preventDefault();

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [datePart, timePart] = slot.datetime.split(", ").map((s) => s.trim());
    const [month, day, year] = datePart.split("/");
    const formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
      2,
      "0"
    )}`;
    const full = `${formattedDate} ${timePart}`;

    const parsed = use24Hour
      ? parse(full, "yyyy-MM-dd HH:mm", new Date())
      : parse(full, "yyyy-MM-dd hh:mm a", new Date());

    if (isNaN(parsed)) return showToast("Invalid time", "error");

    const iso = fromZonedTime(parsed, timezone).toISOString();

    if (!isSlotBookable()) return;

    if (!formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      return;
    }

    const payload = {
      user_name: name,
      email,
      notes,
      guests,
      booked_at: iso,
      custom_answers: customAnswers,
    };
    if (bookingId) payload.bookingId = bookingId;

    try {
      const bookRes = bookingId
        ? await axios.put(
            `${API_BASE_URL}/event/${slug}/book/${bookingId}`,
            payload
          )
        : await axios.post(`${API_BASE_URL}/event/${slug}/book`, payload);

      const { bookingId: newBookingId } = bookRes.data;

      const payRes = await axios.get(
        `${API_BASE_URL}/event/${slug}/payment-settings`
      );
      const pay = payRes.data;

      const needsPayment =
        pay.payment_amount > 0 && (pay.enable_razorpay || pay.enable_stripe);
      if (!needsPayment) {
        showToast("Booking confirmed!");
        redirectOrClose();
        return;
      }

      const eventFee = parseFloat(pay.payment_amount);
      const commission = eventFee * 0.1;
      const total = eventFee + commission;

      setPaymentInfo({
        eventFee,
        commission,
        total,
        bookingId: newBookingId,
        eventId,
        slug,
        enable_razorpay: !!pay.enable_razorpay,
        enable_stripe: !!pay.enable_stripe,
      });

      if (pay.enable_razorpay && pay.enable_stripe) {
        setShowGatewayModal(true);
      } else if (pay.enable_razorpay) {
        setSelectedGateway("razorpay");
        initiateRazorpayPayment({ ...paymentInfo, bookingId: newBookingId });
      } else if (pay.enable_stripe) {
        setSelectedGateway("stripe");
        initiateStripePayment({ ...paymentInfo, bookingId: newBookingId });
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Booking failed";

      console.error("Booking error:", err.response?.data); // DEBUG
      showToast(errorMessage, "error");
    }
  };

  /* ---------- RAZORPAY FLOW ---------- */
  const initiateRazorpayPayment = async (info) => {
    if (!razorpayLoaded) return showToast("Razorpay not ready", "error");

    try {
      const res = await axios.post(
        `${API_BASE_URL}/event/${slug}/initiate-payment`,
        {
          bookingId: info.bookingId,
          gateway: "razorpay",
        }
      );

      const { orderId, amount, paymentId } = res.data;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount,
        currency: "INR",
        name: "TickTickGo",
        description: `Event – ${slug}`,
        order_id: orderId,
        handler: async (response) => {
          await axios.post(`${API_BASE_URL}/event/razorpay-callback`, {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            paymentId,
          });
          showToast("Payment successful!");
          redirectOrClose();
        },
        prefill: { name, email },
        theme: { color: "#0066cc" },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      showToast(err.response?.data?.message || "Razorpay init failed", "error");
    }
  };

  /* ---------- STRIPE FLOW ---------- */
  const initiateStripePayment = async (info) => {
    setStripeRedirecting(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/event/${info.slug}/initiate-payment`,
        {
          bookingId: info.bookingId,
          gateway: "stripe",
        }
      );

      // Redirect to Stripe Checkout
      window.location.href = res.data.sessionUrl;
    } catch (err) {
      setStripeRedirecting(false);
      showToast(err.response?.data?.message || "Stripe init failed", "error");
    }
  };

  const redirectOrClose = () => {
    if (
      eventSettings.redirect_on_booking === "1" &&
      eventSettings.redirectUrl
    ) {
      window.location.href = eventSettings.redirectUrl;
    } else {
      onHide();
    }
  };

  const isSlotBookable = () => {
    const now = new Date();
    const slotDate = new Date(slot.datetime);

    if (eventLimits.minimum_notice_value && eventLimits.minimum_notice_unit) {
      const ms = { minute: 6e4, hour: 36e5, day: 864e5 };
      const notice =
        eventLimits.minimum_notice_value *
        (ms[eventLimits.minimum_notice_unit] || 0);
      if (slotDate - now < notice) {
        showToast(
          `Book at least ${eventLimits.minimum_notice_value} ${eventLimits.minimum_notice_unit}(s) ahead.`,
          "warning"
        );
        return false;
      }
    }

    if (eventLimits.limit_future_bookings) {
      const limit = new Date();
      limit.setDate(
        limit.getDate() + parseInt(eventLimits.limit_future_bookings)
      );
      if (slotDate > limit) {
        showToast(
          `Can book only up to ${eventLimits.limit_future_bookings} days ahead.`,
          "warning"
        );
        return false;
      }
    }

    if (!checkBookingFrequency()) {
      showToast("Frequency limit reached.", "warning");
      return false;
    }

    if (eventLimits.slot_interval) {
      const min = new Date(slot.datetime).getMinutes();
      if (min % eventLimits.slot_interval !== 0) {
        showToast(
          `Only ${eventLimits.slot_interval}-minute intervals allowed.`,
          "warning"
        );
        return false;
      }
    }
    return true;
  };

  const checkBookingFrequency = () => {
    const now = new Date();
    const today = bookingHistory.filter(
      (d) => new Date(d).toDateString() === now.toDateString()
    ).length;
    const week = bookingHistory.filter((d) => {
      const date = new Date(d);
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      return date >= start;
    }).length;
    const month = bookingHistory.filter(
      (d) =>
        d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
    ).length;

    return !(
      (eventLimits.limit_booking_per_day &&
        today >= eventLimits.limit_booking_per_day) ||
      (eventLimits.limit_booking_per_week &&
        week >= eventLimits.limit_booking_per_week) ||
      (eventLimits.limit_booking_per_month &&
        month >= eventLimits.limit_booking_per_month)
    );
  };

  if (!show || !slot) return null;

  return (
    <>
      {/* ---------- MAIN BOOKING MODAL ---------- */}
      <Modal
        show={show && !showGatewayModal && !showRazorpayModal}
        onHide={onHide}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm your details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {slot.day_of_week}, {slot.time}, {slot.datetime}
            {eventSettings.timezone_lock && (
              <small className="text-muted">
                {" "}
                (Timezone: {eventSettings.timezone_lock})
              </small>
            )}
          </p>
          <Form ref={formRef}>
            <Form.Group className="mb-2">
              <Form.Label>Your name *</Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Email address *</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Additional notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Add guests</Form.Label>
              <Form.Control
                value={guests}
                onChange={(e) => setGuests(e.target.value)}
              />
            </Form.Group>

            {customFields.map((f) => (
              <Form.Group key={f.id} className="mb-2">
                <Form.Label>
                  {f.label}
                  {parseInt(f.required) === 1 && " *"}
                </Form.Label>
                <Form.Control
                  type={f.input_type || "text"}
                  placeholder={f.placeholder}
                  required={parseInt(f.required) === 1}
                  value={customAnswers[f.id] || ""}
                  onChange={(e) =>
                    handleCustomFieldChange(f.id, e.target.value)
                  }
                />
              </Form.Group>
            ))}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleConfirm}>
            Confirm Booking
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---------- GATEWAY SELECTION MODAL (both enabled) ---------- */}
      <Modal
        show={showGatewayModal}
        onHide={() => setShowGatewayModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Choose Payment Gateway</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>Event Fee:</strong> ₹{paymentInfo?.eventFee?.toFixed(2)}
            <br />
            <strong>10% Commission:</strong> ₹
            {paymentInfo?.commission?.toFixed(2)}
            <br />
            <strong>Total:</strong> ₹{paymentInfo?.total?.toFixed(2)}
          </Alert>

          <Form>
            {paymentInfo?.enable_razorpay && (
              <FormCheck
                type="radio"
                id="razorpay"
                label="Pay with Razorpay"
                name="gateway"
                checked={selectedGateway === "razorpay"}
                onChange={() => setSelectedGateway("razorpay")}
              />
            )}
            {paymentInfo?.enable_stripe && (
              <FormCheck
                type="radio"
                id="stripe"
                label="Pay with Stripe"
                name="gateway"
                checked={selectedGateway === "stripe"}
                onChange={() => setSelectedGateway("stripe")}
              />
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowGatewayModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!selectedGateway}
            onClick={() => {
              setShowGatewayModal(false);
              if (selectedGateway === "razorpay") {
                initiateRazorpayPayment(paymentInfo);
              } else {
                initiateStripePayment(paymentInfo);
              }
            }}
          >
            {selectedGateway === "razorpay"
              ? razorpayLoaded
                ? "Pay with Razorpay"
                : "Loading..."
              : stripeRedirecting
              ? "Redirecting..."
              : "Pay with Stripe"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---------- (Optional) Razorpay Summary Modal – not used now, but kept for future ---------- */}
      {/* 
      <Modal show={showRazorpayModal} onHide={() => setShowRazorpayModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Payment Summary</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {paymentInfo ? (
            <>
              <Alert variant="info">
                <strong>Event Fee:</strong> ₹{paymentInfo.eventFee.toFixed(2)}<br />
                <strong>10% Commission:</strong> ₹{paymentInfo.commission.toFixed(2)}<br />
                <strong>Total Payable:</strong> ₹{paymentInfo.total.toFixed(2)}
              </Alert>
              <p className="text-muted small">
                You will be redirected to Razorpay to complete the payment.
              </p>
            </>
          ) : (
            <Spinner animation="border" />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRazorpayModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={initiateRazorpayPayment}
            disabled={!razorpayLoaded || !paymentInfo}
          >
            {razorpayLoaded ? "Pay with Razorpay" : "Loading..."}
          </Button>
        </Modal.Footer>
      </Modal> */}
    </>
  );
}

// import React, { useState, useEffect, useRef } from "react";
// import {
//   Modal,
//   Button,
//   Form,
//   Spinner,
//   Alert,
//   FormCheck,
// } from "react-bootstrap";
// import { fromZonedTime } from "date-fns-tz";
// import { parse } from "date-fns";
// import axios from "axios";
// import Swal from "sweetalert2";
// import API_BASE_URL from "../config";

// export const showToast = (title, icon = "success") => {
//   Swal.fire({
//     toast: true,
//     position: "top-end",
//     showConfirmButton: false,
//     timer: 3000,
//     timerProgressBar: true,
//     icon,
//     title,
//   });
// };

// export default function TimeSlotModal({
//   show,
//   onHide,
//   slot,
//   slug,
//   bookingId,
//   eventSettings = {},
//   eventLimits = {},
//   eventId,
//   use24Hour,
// }) {
//   /* ---------- Form state ---------- */
//   const [name, setName] = useState("");
//   const [email, setEmail] = useState("");
//   const [notes, setNotes] = useState("");
//   const [guests, setGuests] = useState("");
//   const [customFields, setCustomFields] = useState([]);
//   const [customAnswers, setCustomAnswers] = useState({});
//   const [totalBookings, setTotalBookings] = useState(0);
//   const [bookingHistory, setBookingHistory] = useState([]);

//   /* ---------- Payment state ---------- */
//   const [paymentInfo, setPaymentInfo] = useState(null); // {eventFee, commission, total, bookingId, eventId}
//   const [showGatewayModal, setShowGatewayModal] = useState(false);
//   const [selectedGateway, setSelectedGateway] = useState(""); // razorpay | stripe
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);
//   const [stripeRedirecting, setStripeRedirecting] = useState(false);

//   const formRef = useRef();

//   /* ---------- Load Razorpay once ---------- */
//   useEffect(() => {
//     const script = document.createElement("script");
//     script.src = "https://checkout.razorpay.com/v1/checkout.js";
//     script.onload = () => setRazorpayLoaded(true);
//     script.onerror = () => showToast("Razorpay failed to load", "error");
//     document.body.appendChild(script);
//     return () => document.body.removeChild(script);
//   }, []);

//   /* ---------- Load totals / history / questions ---------- */
//   useEffect(() => {
//     axios
//       .get(`${API_BASE_URL}/event/${eventId}/total_bookings`)
//       .then((r) => setTotalBookings(r.data.count))
//       .catch(() => {});
//   }, [eventId]);

//   useEffect(() => {
//     if (email && eventId) {
//       axios
//         .post(`${API_BASE_URL}/event/check-frequency`, { email, event_id: eventId })
//         .then((r) => setBookingHistory(r.data.history))
//         .catch(() => {});
//     }
//   }, [email, eventId]);

//   useEffect(() => {
//     if (show && slug) {
//       axios
//         .get(`${API_BASE_URL}/event/${slug}/questions`)
//         .then((r) => setCustomFields(r.data))
//         .catch(() => {});
//     }
//   }, [show, slug]);

//   /* ---------- Helper: custom field change ---------- */
//   const handleCustomFieldChange = (id, value) => {
//     setCustomAnswers((prev) => ({ ...prev, [id]: value }));
//   };

//   /* ---------- BOOKING + PAYMENT TRIGGER ---------- */
//   const handleConfirm = async (e) => {
//     e.preventDefault();

//     /* ---- Parse slot datetime ---- */
//     const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
//     const [datePart, timePart] = slot.datetime.split(", ").map((s) => s.trim());
//     const [month, day, year] = datePart.split("/");
//     const formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
//     const full = `${formattedDate} ${timePart}`;

//     const parsed = use24Hour
//       ? parse(full, "yyyy-MM-dd HH:mm", new Date())
//       : parse(full, "yyyy-MM-dd hh:mm a", new Date());

//     if (isNaN(parsed)) return showToast("Invalid time", "error");

//     const iso = fromZonedTime(parsed, timezone).toISOString();

//     /* ---- Slot checks ---- */
//     if (!isSlotBookable()) return;

//     /* ---- Form validation ---- */
//     if (!formRef.current.checkValidity()) {
//       formRef.current.reportValidity();
//       return;
//     }

//     const payload = {
//       user_name: name,
//       email,
//       notes,
//       guests,
//       booked_at: iso,
//       custom_answers: customAnswers,
//     };
//     if (bookingId) payload.bookingId = bookingId;

//     try {
//       const bookRes = bookingId
//         ? await axios.put(`${API_BASE_URL}/event/${slug}/book/${bookingId}`, payload)
//         : await axios.post(`${API_BASE_URL}/event/${slug}/book`, payload);

//       const { bookingId: newBookingId } = bookRes.data;

//       /* ---- Fetch payment settings ---- */
//       const payRes = await axios.get(`${API_BASE_URL}/event/${slug}/payment-settings`);
//       const pay = payRes.data;

//       const needsPayment = pay.payment_amount > 0 && (pay.enable_razorpay || pay.enable_stripe);
//       if (!needsPayment) {
//         showToast("Booking confirmed!");
//         redirectOrClose();
//         return;
//       }

//       /* ---- Calculate 10% commission ---- */
//       const eventFee = parseFloat(pay.payment_amount);
//       const commission = eventFee * 0.10;
//       const total = eventFee + commission;

//       setPaymentInfo({
//         eventFee,
//         commission,
//         total,
//         bookingId: newBookingId,
//         eventId,
//         slug,
//         enable_razorpay: !!pay.enable_razorpay,
//         enable_stripe: !!pay.enable_stripe,
//       });

//       /* ---- Determine which gateway(s) to show ---- */
//       if (pay.enable_razorpay && pay.enable_stripe) {
//         setShowGatewayModal(true);               // selector modal
//       } else if (pay.enable_razorpay) {
//         setSelectedGateway("razorpay");
//         initiateRazorpayPayment({ ...paymentInfo, bookingId: newBookingId });
//       } else if (pay.enable_stripe) {
//         setSelectedGateway("stripe");
//         initiateStripePayment({ ...paymentInfo, bookingId: newBookingId });
//       }

//     } catch (err) {
//       showToast(err.response?.data?.message || "Booking failed", "error");
//     }
//   };

//   /* ---------- RAZORPAY FLOW ---------- */
//   const initiateRazorpayPayment = async (info) => {
//     if (!razorpayLoaded) return showToast("Razorpay not ready", "error");

//     try {
//       const res = await axios.post(`${API_BASE_URL}/payments/initiate-event`, {
//         eventId: info.eventId,
//         bookingId: info.bookingId,
//         totalAmount: Math.round(info.total * 100), // paise
//       });

//       const { orderId, amount, paymentId } = res.data;

//       const options = {
//         key: process.env.REACT_APP_RAZORPAY_KEY_ID,
//         amount,
//         currency: "INR",
//         name: "TickTickGo",
//         description: `Event – ${slug}`,
//         order_id: orderId,
//         handler: async (response) => {
//           await axios.post(`${API_BASE_URL}/payments/callback-event`, {
//             razorpay_payment_id: response.razorpay_payment_id,
//             razorpay_order_id: response.razorpay_order_id,
//             razorpay_signature: response.razorpay_signature,
//             paymentId,
//           });
//           showToast("Payment successful!");
//           redirectOrClose();
//         },
//         prefill: { name, email },
//         theme: { color: "#0066cc" },
//       };
//       const rzp = new window.Razorpay(options);
//       rzp.open();
//     } catch (err) {
//       showToast(err.response?.data?.message || "Razorpay init failed", "error");
//     }
//   };

//   /* ---------- STRIPE FLOW ---------- */
//   const initiateStripePayment = async (info) => {
//     setStripeRedirecting(true);
//     try {
//       const res = await axios.post(`${API_BASE_URL}/payments/stripe/create-session`, {
//         eventId: info.eventId,
//         bookingId: info.bookingId,
//         totalAmount: Math.round(info.total * 100), // cents
//         successUrl: `${window.location.origin}/event/${slug}?stripe_success=1&bookingId=${info.bookingId}`,
//         cancelUrl: `${window.location.origin}/event/${slug}?stripe_cancel=1`,
//       });

//       window.location.href = res.data.sessionUrl;
//     } catch (err) {
//       setStripeRedirecting(false);
//       showToast(err.response?.data?.message || "Stripe init failed", "error");
//     }
//   };

//   /* ---------- Final redirect/close ---------- */
//   const redirectOrClose = () => {
//     if (eventSettings.redirect_on_booking === "1" && eventSettings.redirectUrl) {
//       window.location.href = eventSettings.redirectUrl;
//     } else {
//       onHide();
//     }
//   };

//   /* ---------- Slot validation (unchanged) ---------- */
//   const isSlotBookable = () => {
//     const now = new Date();
//     const slotDate = new Date(slot.datetime);

//     if (eventLimits.minimum_notice_value && eventLimits.minimum_notice_unit) {
//       const ms = { minute: 6e4, hour: 36e5, day: 864e5 };
//       const notice = eventLimits.minimum_notice_value * (ms[eventLimits.minimum_notice_unit] || 0);
//       if (slotDate - now < notice) {
//         showToast(`Book at least ${eventLimits.minimum_notice_value} ${eventLimits.minimum_notice_unit}(s) ahead.`, "warning");
//         return false;
//       }
//     }

//     if (eventLimits.limit_future_bookings) {
//       const limit = new Date();
//       limit.setDate(limit.getDate() + parseInt(eventLimits.limit_future_bookings));
//       if (slotDate > limit) {
//         showToast(`Can book only up to ${eventLimits.limit_future_bookings} days ahead.`, "warning");
//         return false;
//       }
//     }

//     if (!checkBookingFrequency()) {
//       showToast("Frequency limit reached.", "warning");
//       return false;
//     }

//     if (eventLimits.slot_interval) {
//       const min = new Date(slot.datetime).getMinutes();
//       if (min % eventLimits.slot_interval !== 0) {
//         showToast(`Only ${eventLimits.slot_interval}-minute intervals allowed.`, "warning");
//         return false;
//       }
//     }
//     return true;
//   };

//   const checkBookingFrequency = () => {
//     const now = new Date();
//     const today = bookingHistory.filter((d) => new Date(d).toDateString() === now.toDateString()).length;
//     const week = bookingHistory.filter((d) => {
//       const date = new Date(d);
//       const start = new Date(now);
//       start.setDate(now.getDate() - now.getDay());
//       return date >= start;
//     }).length;
//     const month = bookingHistory.filter((d) => d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()).length;

//     return !(
//       (eventLimits.limit_booking_per_day && today >= eventLimits.limit_booking_per_day) ||
//       (eventLimits.limit_booking_per_week && week >= eventLimits.limit_booking_per_week) ||
//       (eventLimits.limit_booking_per_month && month >= eventLimits.limit_booking_per_month)
//     );
//   };

//   /* ---------- UI ---------- */
//   if (!show || !slot) return null;

//   return (
//     <>
//       {/* ---------- MAIN BOOKING MODAL ---------- */}
//       <Modal show={show && !showGatewayModal && !showRazorpayModal} onHide={onHide}>
//         <Modal.Header closeButton>
//           <Modal.Title>Confirm your details</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <p>
//             {slot.day_of_week}, {slot.time}, {slot.datetime}
//             {eventSettings.timezone_lock && (
//               <small className="text-muted"> (Timezone: {eventSettings.timezone_lock})</small>
//             )}
//           </p>
//           <Form ref={formRef}>
//             <Form.Group className="mb-2">
//               <Form.Label>Your name *</Form.Label>
//               <Form.Control value={name} onChange={(e) => setName(e.target.value)} required />
//             </Form.Group>

//             <Form.Group className="mb-2">
//               <Form.Label>Email address *</Form.Label>
//               <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
//             </Form.Group>

//             <Form.Group className="mb-2">
//               <Form.Label>Additional notes</Form.Label>
//               <Form.Control as="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
//             </Form.Group>

//             <Form.Group className="mb-2">
//               <Form.Label>Add guests</Form.Label>
//               <Form.Control value={guests} onChange={(e) => setGuests(e.target.value)} />
//             </Form.Group>

//             {customFields.map((f) => (
//               <Form.Group key={f.id} className="mb-2">
//                 <Form.Label>
//                   {f.label}{parseInt(f.required) === 1 && " *"}
//                 </Form.Label>
//                 <Form.Control
//                   type={f.input_type || "text"}
//                   placeholder={f.placeholder}
//                   required={parseInt(f.required) === 1}
//                   value={customAnswers[f.id] || ""}
//                   onChange={(e) => handleCustomFieldChange(f.id, e.target.value)}
//                 />
//               </Form.Group>
//             ))}
//           </Form>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="secondary" onClick={onHide}>Cancel</Button>
//           <Button variant="success" onClick={handleConfirm}>Confirm Booking</Button>
//         </Modal.Footer>
//       </Modal>

//       {/* ---------- GATEWAY SELECTION MODAL (both enabled) ---------- */}
//       <Modal show={showGatewayModal} onHide={() => setShowGatewayModal(false)} centered>
//         <Modal.Header closeButton>
//           <Modal.Title>Choose Payment Gateway</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Alert variant="info">
//             <strong>Event Fee:</strong> ₹{paymentInfo?.eventFee?.toFixed(2)}<br />
//             <strong>10% Commission:</strong> ₹{paymentInfo?.commission?.toFixed(2)}<br />
//             <strong>Total:</strong> ₹{paymentInfo?.total?.toFixed(2)}
//           </Alert>

//           <Form>
//             {paymentInfo?.enable_razorpay && (
//               <FormCheck
//                 type="radio"
//                 id="razorpay"
//                 label="Pay with Razorpay"
//                 name="gateway"
//                 checked={selectedGateway === "razorpay"}
//                 onChange={() => setSelectedGateway("razorpay")}
//               />
//             )}
//             {paymentInfo?.enable_stripe && (
//               <FormCheck
//                 type="radio"
//                 id="stripe"
//                 label="Pay with Stripe"
//                 name="gateway"
//                 checked={selectedGateway === "stripe"}
//                 onChange={() => setSelectedGateway("stripe")}
//               />
//             )}
//           </Form>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="secondary" onClick={() => setShowGatewayModal(false)}>Cancel</Button>
//           <Button
//             variant="primary"
//             disabled={!selectedGateway}
//             onClick={() => {
//               setShowGatewayModal(false);
//               if (selectedGateway === "razorpay") initiateRazorpayPayment(paymentInfo);
//               else initiateStripePayment(paymentInfo);
//             }}
//           >
//             {selectedGateway === "razorpay"
//               ? (razorpayLoaded ? "Pay with Razorpay" : "Loading...")
//               : stripeRedirecting
//               ? "Redirecting..."
//               : "Pay with Stripe"}
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   );
// }
